import axios from "axios";
import config from '../config';
import {
  peekStoredInteractionUid,
  clearInteractionUid,
} from "../oidcInteraction";

function backendRequest(propsUrl, data, method) {
  const base = config.REACT_APP_API_URL || "";
  let url = base + propsUrl;
  if (!propsUrl) throw new Error("requests can not be made without a url");
  if (propsUrl.includes("http")) url = propsUrl;
  const requestConfig = {
    // timeout: 5000,
    url,
    data,
    method,
  };
  return axios(requestConfig)
    .then(({ data }) => {
      if (!data) throw new Error(`no data retrieved from call - ${url}`);
      return data;
    })
    .catch((e) => {
      const { message, response } = e;
      console.error(`server error in - ${url}
      ${message}
      `);
      return { error: message, ...response?.data };
    });
}

/** Same-origin OIDC calls (login ingress proxies /oidc → modulesBackend). */
function oidcRequest(path, data, method = "GET") {
  const requestConfig = {
    url: path,
    method,
    withCredentials: true,
    ...(data !== undefined ? { data } : {}),
  };
  return axios(requestConfig)
    .then(({ data: body }) => body)
    .catch((e) => {
      const { message, response } = e;
      const body = response?.data || {};
      console.error(`OIDC error in - ${path}
      ${message}
      `);
      return {
        error: body.message || body.error || message || "OIDC request failed",
        ...body,
      };
    });
}

export async function getLanguages() {
  const languages = await backendRequest("/api/language");
  return languages;
}

export async function login(form = {}) {
  try {
    const {
      isMacau = false,
      password = "",
      "dist-id": repDID = "",
      guid = "",
      onBehalfRepGuid = "",
      loggedRepGuid = "",
      onBehalfCustomerGuid = "",
      redirectUrl = "",
      referer = "",
      store = "",
    } = form;

    // Belt-and-suspenders: never hit Multipass/China Tools while an OIDC
    // interaction is pending (dev RCA: SSO abandoned for /api/login/shopify).
    const pendingInteraction = peekStoredInteractionUid();
    if (pendingInteraction && (repDID?.length || password?.length)) {
      console.warn(
        "[login] refusing /api/login/shopify; finishing OIDC interaction instead",
        pendingInteraction
      );
      return finishOidcInteraction({
        uid: pendingInteraction,
        username: repDID,
        password,
      }).then((result) => {
        if (result.redirectTo) {
          clearInteractionUid();
          return { url: result.redirectTo };
        }
        return { error: result.error || "Unable to login please try again" };
      });
    }

    console.info("[login] POST /api/login/shopify");
    const response = await backendRequest(
      `/api/login/shopify`,
      {
        repDID,
        password,
        guid,
        onBehalfRepGuid,
        loggedRepGuid,
        onBehalfCustomerGuid,
        redirectUrl,
        referer,
        store,
      },
      "POST"
    );
    const { error: _error, message, url } = response;
    const finalUrl = url === "none" ? "" : url;
    const error = finalUrl
      ? undefined
      : message || "Unable to login please try again";
    console.warn("login failure message - " + _error);
    return {
      error,
      url,
    };
  } catch (e) {
    console.warn(e);
    return { error: "Unable to login please try again" };
  }
}

/** Validate an OIDC interaction session (cookies via login-host /oidc ingress). */
export async function getOidcInteraction(uid) {
  if (!uid) return { error: "missing_interaction" };
  const response = await oidcRequest(`/oidc/interaction/${encodeURIComponent(uid)}`);
  if (response?.error && !response?.uid) {
    return {
      error:
        response.message ||
        response.error ||
        "Session expired. Please restart login from the store.",
    };
  }
  return response;
}

/** Finish OIDC login + auto-consent; returns { redirectTo } or { error }. */
export async function finishOidcInteraction({ uid, username, password }) {
  if (!uid) return { error: "missing_interaction" };
  if (!username?.length || !password?.length) {
    return { error: "username and password are required" };
  }
  console.info("[login] POST /oidc/interaction/:uid/login", uid);
  const response = await oidcRequest(
    `/oidc/interaction/${encodeURIComponent(uid)}/login`,
    { username, password },
    "POST"
  );
  if (response?.redirectTo) {
    clearInteractionUid();
    return { redirectTo: response.redirectTo };
  }
  return {
    error:
      response?.message ||
      response?.error ||
      "Unable to login please try again",
  };
}

export async function resetPassword(repDID, customerDID, token, password) {
  const response = await backendRequest(
    `/api/login/reset-password`,
    {
      repDID,
      customerDID,
      token,
      password,
    },
    "post"
  );
  return response;
}
export async function forgotPassword({ email, locale }) {
  const response = await backendRequest(
    `/api/login/forgot-password`,
    { email, lang: locale },
    "post"
  );
  return response;
}
